import * as yup from "yup";
import Image from "next/image";
import Toast from "@components/toast";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "next-i18next";
import { useSettings } from "@hooks/settings";
import { yupResolver } from "@hookform/resolvers/yup";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

const schema = yup.object().shape({
  email: yup.string().email().required(),
});

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["auth"])),
    },
  };
}

export default function ResetPassword() {
  const [loadingForm, setLoadingForm] = useState(false);
  const { t } = useTranslation("auth");
  const router = useRouter();
  const notify = React.useCallback((type, message) => {
    Toast({ type, message });
  }, []);
  const settings = useSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoadingForm(true);
    const res = await fetch("/api/auth/password/reset", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) {
      notify("error", json.message);
    } else {
      notify("success", json.message);
      setTimeout(() => {
        router.push(
          `${
            router.locale === router.defaultLocale ? "" : `/${router.locale}`
          }/auth/signin`
        );
      }, 8000);
    }
    setLoadingForm(false);
  };

  return (
    <>
      <div className="container">
        <div className="row justify-content-center">
          <div className="d-none d-md-block col-md-8 col-lg-6 col-xl-4">
            <Image
              src="/images/forgot-password-animate.svg"
              alt="Reset Password"
              width={400}
              height={400}
              priority
              className="d-block mx-auto mb-4"
              style={{ width: "auto", height: "auto" }}
            />
          </div>

          <div className="col-12 col-md-8 col-lg-6 col-xl-4">
            <div className="card" style={{ borderRadius: "6px" }}>
              <div className="card-header">
                <Image
                  src={settings.app_logo}
                  alt={settings.app_name}
                  width={100}
                  height={100}
                  priority
                  className="d-block mx-auto mb-4"
                  style={{ width: "auto", height: "auto" }}
                />
                <h1 className="display-6 text-center mb-3">
                  {t("auth:resetPasswordTitle")}
                </h1>
              </div>

              <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      {t("fields.email.label")}
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      {...register("email")}
                    />
                    {errors.email && (
                      <div className="text-danger">{errors.email.message}</div>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="btn btn-bota-light-blue"
                    disabled={loadingForm}
                  >
                    {t("resetPassword")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
